import { useEffect, useState } from 'react'

import { Button } from 'components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { cn } from 'lib/utils'

import { GRAIN_LABELS, HOME_TEMPLATE_SCOPE_BY_GRAIN, TEMPLATE_TABS, templateTabsForGrain } from '../constants'
import type { ExplorerReportVisibility, ExplorerTemplateScope } from '../types/explorer.types'

export type SaveReportMode = 'create' | 'saveAs' | 'rename' | 'saveAsTemplate'

interface SaveReportDialogProps {
	open: boolean
	mode: SaveReportMode
	/** Pre-fills the field: the loaded report's name for rename and save-as. */
	initialName?: string
	isSaving?: boolean
	error?: string | null
	onClose: () => void
	/** `templateScope` is set only in `saveAsTemplate` mode. */
	onSubmit: (name: string, visibility: ExplorerReportVisibility, templateScope?: ExplorerTemplateScope) => void
	/** The loaded report's visibility, when renaming or copying one. */
	initialVisibility?: ExplorerReportVisibility
	/** The report's grain: decides which kinds of asset a template can be used on. */
	grain?: string
	/** Pre-selects where the template is filed; defaults to the grain's own tab. */
	initialTemplateScope?: ExplorerTemplateScope | null
}

const TAB_LABEL = Object.fromEntries(TEMPLATE_TABS.map(tab => [tab.value, tab.label])) as Record<string, string>

/**
 * How each filing option reads. Under an asset type, the template is used on
 * one such asset; under the grain's own tab, it runs across everything.
 */
const templateOptionLabel = (grain: string, tab: ExplorerTemplateScope) => {
	const rows = (GRAIN_LABELS[grain] ?? 'rows').toLowerCase()
	const scoped = tab !== HOME_TEMPLATE_SCOPE_BY_GRAIN[grain] || grain === 'evaluationSite'
	return scoped ? `${TAB_LABEL[tab]}: ${rows} of one ${tab}` : `${TAB_LABEL[tab]}: all your ${rows}`
}

const COPY: Record<SaveReportMode, { title: string; description: string; action: string }> = {
	create: {
		title: 'Save report',
		description: 'Give this layout a name so you can come back to it.',
		action: 'Save',
	},
	saveAs: {
		title: 'Save as a new report',
		description: 'Keeps the original untouched and saves your changes under a new name.',
		action: 'Save as new',
	},
	rename: {
		title: 'Rename report',
		description: 'Only the name changes. The columns, filters and sorting stay as they are.',
		action: 'Rename',
	},
	saveAsTemplate: {
		title: 'Save as template',
		description: 'Keeps this layout in your template gallery, to start new reports from.',
		action: 'Save template',
	},
}

/**
 * Name a report, on the way to creating, copying or renaming it (MYD-363).
 *
 * One dialog for all three because they differ only in wording and in what the
 * caller does with the name. Three near-identical components would drift.
 *
 * `components/ui/dialog.tsx` is a hand-rolled portal, NOT Radix: its prop is
 * `onClose`, not `onOpenChange`, and it renders nothing when closed rather than
 * animating out.
 */
export function SaveReportDialog({
	open,
	mode,
	initialName = '',
	initialVisibility = 'personal',
	isSaving = false,
	error,
	onClose,
	onSubmit,
	grain = '',
	initialTemplateScope = null,
}: SaveReportDialogProps) {
	const asTemplate = mode === 'saveAsTemplate'
	const tabOptions = templateTabsForGrain(grain)
	const defaultTab = (initialTemplateScope && tabOptions.includes(initialTemplateScope)
		? initialTemplateScope
		: HOME_TEMPLATE_SCOPE_BY_GRAIN[grain]) as ExplorerTemplateScope | undefined

	const [name, setName] = useState(initialName)
	const [visibility, setVisibility] = useState<ExplorerReportVisibility>(initialVisibility)
	const [templateScope, setTemplateScope] = useState<ExplorerTemplateScope | undefined>(defaultTab)

	// Reset when the dialog opens, not on every render. Without this, opening
	// Rename after a Save-as would show the name typed into the previous one.
	useEffect(() => {
		if (open) {
			setName(initialName)
			setVisibility(initialVisibility)
			setTemplateScope(defaultTab)
		}
	}, [open, initialName, initialVisibility, defaultTab])

	const copy = COPY[mode]
	const trimmed = name.trim()
	// A template must be filed somewhere this grain allows; the server would
	// 400 otherwise, and the dialog is the place to say so.
	const templateScopeValid = !asTemplate || (templateScope !== undefined && tabOptions.includes(templateScope))
	const canSubmit = trimmed.length > 0 && templateScopeValid && !isSaving

	const submit = () => {
		if (!canSubmit) return
		if (asTemplate) onSubmit(trimmed, visibility, templateScope)
		else onSubmit(trimmed, visibility)
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="sm:max-w-[440px]">
				<DialogHeader>
					<DialogTitle>{copy.title}</DialogTitle>
					<DialogDescription>{copy.description}</DialogDescription>
				</DialogHeader>

				<div className="px-5 py-4 space-y-4">
					<div className="space-y-2">
						<Label htmlFor="explorer-report-name">Name</Label>
						<Input
							id="explorer-report-name"
							value={name}
							autoFocus
							onChange={event => setName(event.target.value)}
							// Enter submits, because typing a name and reaching for the
							// mouse is the wrong shape for a one-field dialog.
							onKeyDown={event => {
								if (event.key === 'Enter') submit()
							}}
							placeholder="Texas big boxes"
						/>
						{error && <p className="text-sm text-destructive">{error}</p>}
					</div>

					{asTemplate && (
						<div className="space-y-2">
							<Label htmlFor="explorer-template-scope">Use it on</Label>
							<Select
								id="explorer-template-scope"
								value={templateScope ?? ''}
								onChange={event => setTemplateScope(event.target.value as ExplorerTemplateScope)}
							>
								{tabOptions.map(tab => (
									<option key={tab} value={tab}>
										{templateOptionLabel(grain, tab)}
									</option>
								))}
							</Select>
							<p className="text-xs text-gray-500">
								Also decides which tab of the gallery it appears under.
							</p>
						</div>
					)}

					<div className="space-y-2">
						<Label>Who can open it</Label>
						{(
							[
								{ value: 'personal', label: 'Only me', hint: 'A private report.' },
								{
									value: 'shared',
									label: 'My partners',
									// The reassurance is the point: without it, "shared"
									// reads as "shares my data", which it never does.
									hint: 'They see the layout. Rows are always their own.',
								},
							] as const
						).map(option => (
							<button
								key={option.value}
								type="button"
								onClick={() => setVisibility(option.value)}
								className={cn(
									'w-full text-left px-3 py-2 rounded-md border transition-colors',
									visibility === option.value
										? 'border-primary bg-primary/5'
										: 'border-gray-200 hover:bg-gray-50',
								)}
							>
								<span className="block text-sm font-medium text-gray-900">{option.label}</span>
								<span className="block text-xs text-gray-500">{option.hint}</span>
							</button>
						))}
					</div>
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={onClose} disabled={isSaving}>
						Cancel
					</Button>
					<Button onClick={submit} disabled={!canSubmit}>
						{isSaving ? 'Saving...' : copy.action}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default SaveReportDialog
